import { useTranslation } from "@/core/i18n";
import { components } from "api/generated";
import { Button } from "atoms/button/Button";
import CameraFlip from "atoms/icon/icons/camera_flip.svg";
import Close from "atoms/icon/icons/close.svg";
import Billing from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import SwapHorizontal from "atoms/icon/icons/ic_swap_horizontal.svg";
import Snowflake from "atoms/icon/icons/ic_snowflake_small_ol.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { TextButton } from "atoms/text-button/TextButton";
import dayjs from "dayjs";
import { SettingsContainer } from "molecules/containers/settings-container/SettingsContainer";
import { SettingsContainerButton } from "molecules/containers/settings-container-button/SettingsContainerButton";
import {
  getDunningDays,
  isFreezePending,
  isFreezeScheduled,
  isMembershipFrozen,
  isMembershipManagedExternally,
} from "pages/root/wallet/Memberships/membershipHelper";
import { useFeatureFlag } from "services/Flags";
import { getBillingIntervalBase, parseBillingPeriodName } from "utils/billingPeriod.helpers";
import { hasCancellationRunway, hasFutureScheduledCancellation } from "utils/membershipStatus";

interface ManageMembershipCardProps {
  disableCancellationAllowed: boolean;
  platformUserMembership: components["schemas"]["PlatformUserMembershipModel"];
  paymentMethodLast4?: string | null;
  onKeepMembership: () => void;
  onUpdatePaymentMethod: () => void;
  onReactivateMembership: () => void;
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"];
  onRetryMembershipPayment: (platformUserMembershipId: string) => void;
  onKeepCurrentMembership: (platformUserMembershipId: string) => void;
  isKeepingCurrentMembership?: boolean;
  isRetryingMembershipPayment?: boolean;
  freeze?: components["schemas"]["DisplayMembershipFreeze"] | null;
  onWithdrawFreezeRequest: (platformUserMembershipId: string, membershipFreezeId: string) => void;
  withdrawPending?: boolean;
  onResumeMembership: (platformUserMembershipId: string, membershipFreezeId: string) => void;
}

export enum MembershipStatus {
  active = "active",
  willCancel = "willCancel",
  canceled = "canceled",
  notAvailable = "notAvailable",
  dunning = "dunning",
  freezePending = "freezePending",
  freezeScheduled = "freezeScheduled",
  frozen = "frozen",
}

export const getMembershipStatus = (
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"],
  membershipActive: boolean,
  freeze?: components["schemas"]["DisplayMembershipFreeze"] | null,
): MembershipStatus => {
  const willBeCanceled = hasFutureScheduledCancellation(membershipStatus.willCancelAt);
  const isCanceled = membershipStatus.canceled;
  const isNotAvailable = isCanceled && membershipActive === false;
  const isInDunning = membershipStatus.dunningStage && membershipStatus.enteredDunningAt;
  // A canceled membership without runway is dead and must not render payment-failure CTAs.
  const isTerminallyCanceled = !hasCancellationRunway(membershipStatus);

  let status: MembershipStatus;

  if (isNotAvailable) {
    status = MembershipStatus.notAvailable;
  } else if (isInDunning && !isTerminallyCanceled) {
    status = MembershipStatus.dunning;
  } else if (willBeCanceled && !isTerminallyCanceled) {
    status = MembershipStatus.willCancel;
  } else if (isCanceled) {
    status = MembershipStatus.canceled;
  } else if (isFreezePending(freeze)) {
    status = MembershipStatus.freezePending;
  } else if (isFreezeScheduled(freeze)) {
    status = MembershipStatus.freezeScheduled;
  } else if (isMembershipFrozen(freeze)) {
    status = MembershipStatus.frozen;
  } else {
    status = MembershipStatus.active;
  }
  return status;
};

export const ManageMembershipCard = ({
  disableCancellationAllowed,
  platformUserMembership,
  paymentMethodLast4,
  onUpdatePaymentMethod,
  onKeepMembership,
  onReactivateMembership,
  membershipStatus,
  onRetryMembershipPayment,
  onKeepCurrentMembership,
  isKeepingCurrentMembership = false,
  isRetryingMembershipPayment = false,
  freeze,
  onWithdrawFreezeRequest,
  withdrawPending,
  onResumeMembership,
}: ManageMembershipCardProps) => {
  const { t } = useTranslation();
  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");
  const pendingChange = platformUserMembership.pendingChange;

  const {
    name,
    active,
    allowFreeze,
    maxFreezeBillingPeriods,
    commitment = 0,
    billingPeriodInterval,
    billingPeriodIntervalCount,
  } = platformUserMembership.membership;

  const baseBillingPeriodInterval = parseBillingPeriodName(billingPeriodInterval);
  const adjustedCommitment = Math.max(commitment - 1, 0);

  const billingPeriodIntervalName = isFlexibleMembershipsEnabled
    ? (getBillingIntervalBase(baseBillingPeriodInterval) as dayjs.ManipulateType)
    : "month";

  const totalCommitmentPeriod =
    adjustedCommitment * (isFlexibleMembershipsEnabled ? billingPeriodIntervalCount : 1);

  const commitmentExpiration = dayjs(platformUserMembership.created).add(
    totalCommitmentPeriod,
    billingPeriodIntervalName,
  );

  const hasCommitmentPeriod = dayjs().isBefore(commitmentExpiration);
  const isExternallyManaged = isMembershipManagedExternally(
    platformUserMembership.paymentProcessorSubscriptionId,
    platformUserMembership.zenotiId,
  );
  const status = getMembershipStatus(membershipStatus, active, freeze);
  const canRequestFreeze =
    status == MembershipStatus.active &&
    allowFreeze &&
    maxFreezeBillingPeriods >= 1 &&
    !platformUserMembership.activeMembershipFreezeId &&
    // A freeze voids the renewal invoices a scheduled change applies on, so the
    // backend rejects freezing while one is pending; don't offer the action.
    !pendingChange;
  const numberOfAttempts = 4 - platformUserMembership.dunningStage;
  const addedDunningDaysForNextPayment = getDunningDays(platformUserMembership.dunningStage);
  const addedDunningDaysForLastPayment = getDunningDays(platformUserMembership.dunningStage - 1);

  const lastManualPaymentAttemptDay = dayjs(platformUserMembership.manualPaymentLastAttemptAt);
  const lastAutomaticPaymentAttemptDay = dayjs(platformUserMembership.enteredDunningAt).add(
    addedDunningDaysForLastPayment,
    "day",
  );

  return (
    <div
      className="flex flex-col rounded-lg bg-white shadow-[0px_2px_10px_rgba(0,0,0,0.06)]"
      data-testid="manage-membership-card"
      data-platform-user-membership-id={platformUserMembership.platformUserMembershipId}
    >
      <div className="flex flex-col gap-y-4">
        <h3 className="flex justify-center pt-5 text-title">{name}</h3>
        {status == MembershipStatus.notAvailable && (
          <div className="flex flex-col gap-y-5 p-5 pt-0">
            <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-red">
              {t("manageMembershipCard.membershipCanceledOn", {
                date: dayjs(platformUserMembership.canceledDate).format("MMM D, YYYY"),
              })}
            </div>
            <InfoBox text={t("manageMembershipCard.noLongerAvailable")} />
          </div>
        )}
        {status == MembershipStatus.canceled && (
          <div className="flex flex-col gap-y-5 pb-5">
            <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-red">
              {t("manageMembershipCard.membershipCanceledOn", {
                date: dayjs(platformUserMembership.canceledDate).format("MMM D, YYYY"),
              })}
            </div>
            <div className="flex justify-center">
              <Button size="small" onClick={onReactivateMembership}>
                {t("manageMembershipCard.reactivate")}
              </Button>
            </div>
          </div>
        )}
        {status == MembershipStatus.willCancel && (
          <div className="flex flex-col gap-y-3 p-4 pt-0">
            <InfoBox
              text={t("manageMembershipCard.cancelingOn", {
                date: dayjs(platformUserMembership.willCancelAt).format("MMM D, YYYY"),
              })}
              type="error"
            />
            <div className="flex justify-center">
              <TextButton onClick={onKeepMembership}>
                {t("manageMembershipCard.keepMyMembership")}
              </TextButton>
            </div>
          </div>
        )}
        {status == MembershipStatus.freezePending && freeze && (
          <div className="flex flex-col gap-y-3 p-4 pt-0">
            <InfoBox leftIcon={Snowflake} text="Freeze pending approval" type="brand" />
            <div className="flex justify-center">
              <TextButton
                disabled={withdrawPending}
                onClick={() =>
                  onWithdrawFreezeRequest(
                    platformUserMembership.platformUserMembershipId,
                    freeze.membershipFreezeId,
                  )
                }
              >
                {t("manageMembershipCard.withdrawFreezeRequest")}
              </TextButton>
            </div>
          </div>
        )}
        {status == MembershipStatus.freezeScheduled && freeze && (
          <div className="flex flex-col gap-y-3 p-4 pt-0">
            <InfoBox
              leftIcon={Snowflake}
              text={`Freeze starts ${dayjs(freeze.startsAt).format("MMM D, YYYY")}`}
              type="brand"
            />
            <div className="flex flex-col items-center gap-y-1">
              <span className="px-3 text-center text-body2 text-secondary">
                {t("manageMembershipCard.freezeScheduledExplanation", {
                  date: dayjs(freeze.resumesAt).format("MMM D, YYYY"),
                })}
              </span>
              <TextButton
                onClick={() =>
                  onResumeMembership(
                    platformUserMembership.platformUserMembershipId,
                    freeze.membershipFreezeId,
                  )
                }
              >
                {t("manageMembershipCard.cancelScheduledFreeze")}
              </TextButton>
            </div>
          </div>
        )}
        {status == MembershipStatus.frozen && freeze && (
          <div className="flex flex-col gap-y-3 p-4 pt-0">
            <InfoBox
              leftIcon={Snowflake}
              text={`Frozen until ${dayjs(freeze.resumesAt).format("MMM D, YYYY")}`}
              type="brand"
            />
            <div className="flex flex-col items-center gap-y-3">
              <span className="px-3 text-center text-body2 text-secondary">
                {t("manageMembershipCard.frozenBillingPaused")}
              </span>
              <Button
                size="small"
                onClick={() =>
                  onResumeMembership(
                    platformUserMembership.platformUserMembershipId,
                    freeze.membershipFreezeId,
                  )
                }
              >
                {t("manageMembershipCard.resumeMembership")}
              </Button>
            </div>
          </div>
        )}
        {(status == MembershipStatus.active || status == MembershipStatus.dunning) && (
          <div className="flex flex-col gap-y-5">
            {status == MembershipStatus.dunning ? (
              <div className="flex flex-col items-center justify-center gap-y-3 px-3">
                <div className="text-bold1 text-red">
                  {t("manageMembershipCard.benefitsDisabled")}
                </div>
                <div className="text-body2 text-red">
                  {t("manageMembershipCard.unableToProcessPayment")}{" "}
                  {paymentMethodLast4 &&
                    `${t("manageMembershipCard.endingIn", { last4: paymentMethodLast4 })} `}
                  {t("manageMembershipCard.onDate", {
                    date:
                      lastManualPaymentAttemptDay >= lastAutomaticPaymentAttemptDay
                        ? lastManualPaymentAttemptDay.format("MMM D, YYYY")
                        : lastAutomaticPaymentAttemptDay.format("MMM D, YYYY"),
                  })}
                </div>
                <div className="text-body2 text-red">
                  {t("manageMembershipCard.willRetryOn", {
                    date: dayjs(platformUserMembership.enteredDunningAt)
                      .add(addedDunningDaysForNextPayment, "day")
                      .format("MMM D, YYYY"),
                  })}
                </div>
                <div className="text-body2 text-red">
                  {t("manageMembershipCard.paymentAttemptsRemainingPrefix")}{" "}
                  <strong>
                    {numberOfAttempts} {t("manageMembershipCard.payment")}{" "}
                    {t("manageMembershipCard.nounAttempt", { count: numberOfAttempts })}{" "}
                  </strong>
                  {t("manageMembershipCard.paymentAttemptsRemainingSuffix")}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-secondary">
                {hasCommitmentPeriod && (
                  <span>
                    {t("manageMembershipCard.minimumCommitmentRemaining", {
                      duration: commitmentExpiration.fromNow(true),
                    })}
                  </span>
                )}
                {!isExternallyManaged && (
                  <span>
                    {t("manageMembershipCard.nextPaymentOn", {
                      date: dayjs(platformUserMembership.expiration).format("MMM D, YYYY"),
                    })}
                    {platformUserMembership.paymentMethodDetails && (
                      <>
                        <br />
                        {t("manageMembershipCard.usingPaymentMethodEndingIn", {
                          last4: platformUserMembership.paymentMethodDetails.last4,
                        })}
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
            {pendingChange && (
              <div className="flex flex-col gap-y-3 p-4 pt-0">
                <InfoBox
                  text={t("manageMembershipCard.switchingTo", {
                    membershipName: pendingChange.toMembershipName,
                    date: dayjs(pendingChange.expectedEffectiveAt).format("MMM D, YYYY"),
                  })}
                />
                <div className="flex justify-center">
                  <TextButton
                    disabled={isKeepingCurrentMembership}
                    onClick={() =>
                      onKeepCurrentMembership(platformUserMembership.platformUserMembershipId)
                    }
                  >
                    {t("manageMembershipCard.keepCurrentMembership")}
                  </TextButton>
                </div>
              </div>
            )}
            <div className="border-black-600 overflow-clip rounded-b-lg border-t">
              {isExternallyManaged ? (
                <div className="px-5 py-4 text-center text-body2 text-secondary">
                  {t("manageMembershipCard.externallyManaged")}
                </div>
              ) : (
                <>
                  {status == MembershipStatus.dunning && (
                    <SettingsContainerButton
                      startIconSVG={CameraFlip}
                      disabled={isRetryingMembershipPayment}
                      onClick={() =>
                        onRetryMembershipPayment(platformUserMembership.platformUserMembershipId)
                      }
                    >
                      {t("manageMembershipCard.retryCurrentPaymentMethod")}
                    </SettingsContainerButton>
                  )}
                  <SettingsContainerButton startIconSVG={Billing} onClick={onUpdatePaymentMethod}>
                    {t("manageMembershipCard.updatePaymentMethod")}
                  </SettingsContainerButton>
                  {status === MembershipStatus.active &&
                    !pendingChange &&
                    platformUserMembership.hasChangeOptions && (
                      <SettingsContainer
                        startIconSVG={SwapHorizontal}
                        to={`/wallet/memberships/manage/${platformUserMembership.platformUserMembershipId}/change`}
                      >
                        {t("manageMembershipCard.changeMembership")}
                      </SettingsContainer>
                    )}
                  {canRequestFreeze && (
                    <SettingsContainer
                      to={`/wallet/memberships/manage/${platformUserMembership.platformUserMembershipId}/freeze`}
                      startIconSVG={Snowflake}
                    >
                      {t("manageMembershipCard.requestFreeze")}
                    </SettingsContainer>
                  )}
                  <SettingsContainer
                    disabled={hasCommitmentPeriod && disableCancellationAllowed}
                    to={`/wallet/memberships/manage/${
                      platformUserMembership.platformUserMembershipId
                    }${status === "dunning" ? "/dunning" : ""}`}
                    startIconSVG={Close}
                  >
                    {t("manageMembershipCard.cancelMembership")}
                  </SettingsContainer>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
